
<!--
* Wrapper for Ms image field to enable upload functionality using cloud functions
*
* @param {string} uploadFolder - The folder to upload the image to
* @param {string} initialImageUrl - The initial url to show as image
* @param {string} defaultImageSrc - The default image url if image hasn't been uploaded
* @param {string} noImageSrc - The url of image to show if a thumbnail image
*                              for uploaded image hasn't been found
* @param {boolean} showControls - Whether to show image controls
* @param {File} file - Image to upload on creation of component
*
* @fires MsImageUpload#uploadSuccess
* @fires MsImageUpload#deleteClick
-->

<!--
* Fires when image has been successfully uploaded
*
* @event MsImageUpload#uploadSuccess
-->

<!--
* Fires on delete Click
*
* @event MsImageUpload#deleteClick
-->

<template>
  <div>
    <ms-file-field
      :imageUrl="image"
      :fallbackUrl="fallbackUrl"
      :acceptedTypes="'image/*'"
      :defaultImageUrl="defaultImageSrc"
      :isProgressing="isProgressing || isPreparing"
      :progress="progress"
      :preparingLabel="isPreparing ? $t('message.imageUpload.preparing') : ''"
      :showControls="shouldShowControls"
      :maxTime="maxTime"
      :retryWait="retryWait"
      :loadingImageSrc="noImagePlaceholder"
      ref="fileField"
      @input="onInput"
      @cancelClick="onCancel"
      @deleteClick="onDelete"
    />
    <div
      v-if="errorMessage"
      class="upload-error"
      role="alert">
      <p class="upload-error__message">{{ errorMessage }}</p>
      <button
        type="button"
        class="upload-error__retry"
        @click="onRetry">{{ $t('message.imageUpload.tryAgain') }}</button>
    </div>
  </div>
</template>

<script>
  import AppSelect from '@/components/app-select'
  import AppSettings from '@/app-settings.json'
  import MsFileField from '@/components/ms-file-field'
  import Cloud, { UPLOAD_ERRORS } from '@/cloud/cloud'
  import { prepareImageForUpload, PrepareError, PrepareErrorCode } from '@/utils/image-prepare'
  // This import loads the firebase namespace along with all its type information.
  import firebase from 'firebase/app'

  export default {
    name: 'MsImageUpload',
    components: {
      MsFileField,
      AppSelect
    },

    props: {
      uploadFolder: {
        type: String,
        required: true
      },
      imageUrl: {
        type: String,
        default: null
      },

      defaultImageSrc: {
        type: String,
        default: null
      },

      noImageSrc: {
        type: String,
        required: true
      },

      showControls: {
        type: Boolean
      },

      file: {
        type: File
      }
    },

    data: function () {
      return {
        image: this.imageUrl,
        fallbackUrl: null,
        storage: firebase.storage(),
        maxTime: AppSettings.imageUpload.maxTime,
        retryWait: AppSettings.imageUpload.retryWait,
        noImagePlaceholder: AppSettings.noImagePlaceholder,
        imageUpload: {
          isProgressing: false,
          isPreparing: false,
          progress: 0
        },
        errorMessage: null,
        shouldShowControls: this.showControls ? this.showControls : false
      }
    },

    computed: {

      progress: {
        get () {
          return this.imageUpload.progress
        },

        set (progress) {
          this.imageUpload.progress = progress
        }
      },

      isProgressing: {
        get () {
          return this.imageUpload.isProgressing
        },

        set (isProgressing) {
          this.imageUpload.isProgressing = isProgressing
        }
      },

      isPreparing: {
        get () {
          return this.imageUpload.isPreparing
        },

        set (isPreparing) {
          this.imageUpload.isPreparing = isPreparing
        }
      }
    },

    created () {
      if (this.file) {
        this.startUpload(this.file)
        this.shouldShowControls = false
      }
    },

    methods: {

      /**
       * attempts to upload image using cloud function
       *
       * @param {Object} event - event of file input
       */
      onInput: function (event) {
        const file = event.target.files[0]
        if (!file) return
        this.startUpload(file)
        this.shouldShowControls = false
      },

      /**
       * Run the client-side prepare pipeline (HEIC -> JPEG, resize, compress)
       * then hand off to Firebase upload.
       *
       * @param {File} file - the file selected by the user
       */
      startUpload: async function (file) {
        this.errorMessage = null
        this.isPreparing = true
        let prepared
        try {
          prepared = await prepareImageForUpload(file)
        } catch (err) {
          this.isPreparing = false
          this.errorMessage = this.mapPrepareError(err)
          console.error('Image prepare failed:', err)
          return
        }
        this.isPreparing = false

        Cloud.uploadImage(
          this.uploadFolder,
          prepared.file,
          this.progressCb,
          this.errorCb,
          this.successCb
        )
      },

      /**
       * Progress callback from upload image cloud function
       *
       * @param {Object} snapshot - Snapshot of the image upload object
       * @param {number} snapshot.progress - Progress of image upload
       * @param {string} snapshot.name - Name of image upload
       * @param {string} snapshot.state - State of image upload
       */
      progressCb: function (snapshot) {
        if (!this.isProgressing) {
          this.isProgressing = true
        }

        this.progress = snapshot.progress
        this.fileName = snapshot.name
      },

      /**
       * Error callback from upload image cloud function
       *
       * @param {string} error - Error state
       */
      errorCb: function (error) {
        this.isProgressing = false
        this.errorMessage = this.mapUploadError(error)
        console.error(error)
      },

      /**
       * Success callback from upload image cloud function
       * Emits upload success event and attempts to get thumbnail for image
       *
       * @param {string} url - Url of uploaded image
       * @param {string} thumbUrl - Url of where thmubnail image will eventually resolve
       */
      successCb: function (url, thumbUrl) {
        this.errorMessage = null
        this.emitUploadSuccess(url)
        this.fallbackUrl = url
        this.image = thumbUrl
        this.isProgressing = false
        this.shouldShowControls = true
      },

      // @Todo handle cancel event
      onCancel: function () {
        console.log('Cancel logic')
      },

      /**
       * Emit delete click
       */
      onDelete: function () {
        this.errorMessage = null
        this.$emit('deleteClick')
        setTimeout(() => {
          this.image = this.imageUrl
          this.fallbackUrl = null
        }, 0)
      },

      /**
       * Re-open the file picker after a failed prepare/upload.
       */
      onRetry: function () {
        this.errorMessage = null
        const fileField = this.$refs.fileField
        const input = fileField && fileField.$el ? fileField.$el.querySelector('input[type="file"]') : null
        if (input) input.click()
      },

      /**
       * Emit upload success
       *
       * @param {string} url - Url of uploaded image
       */
      emitUploadSuccess: function (url) {
        this.$emit('uploadSuccess', url)
      },

      /**
       * Map a PrepareError to a user-facing translated message.
       */
      mapPrepareError: function (err) {
        const code = err instanceof PrepareError ? err.code : null
        switch (code) {
          case PrepareErrorCode.UNSUPPORTED_TYPE:
            return this.$t('message.imageUpload.errors.unsupportedType')
          case PrepareErrorCode.HEIC_CONVERSION_FAILED:
            return this.$t('message.imageUpload.errors.heicFailed')
          case PrepareErrorCode.TOO_LARGE:
            return this.$t('message.imageUpload.errors.tooLarge')
          case PrepareErrorCode.COMPRESSION_FAILED:
          default:
            return this.$t('message.imageUpload.errors.unknown')
        }
      },

      /**
       * Map an UPLOAD_ERRORS code to a user-facing translated message.
       */
      mapUploadError: function (error) {
        switch (error) {
          case UPLOAD_ERRORS.UNAUTHORIZED:
            return this.$t('message.imageUpload.errors.unauthorized')
          case UPLOAD_ERRORS.CANCELLED:
            return null
          case UPLOAD_ERRORS.UNKNOWN:
          default:
            return this.$t('message.imageUpload.errors.unknown')
        }
      }
    }
  }
</script>


<style lang="scss" scoped>
  @import "../assets/sass/shared.scss";

  .upload-error {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: $dims-field-error-margin;
    margin-top: $dims-field-error-margin;
    padding: $dims-field-error-margin $dims-spacing-small;
    background: $quaternary-brand-color;
    border: 1px solid $error-styling-color;
    color: $primary-text-color;
    font-size: $medium-font-size;

    &__message {
      margin: 0;
      flex: 1;
    }

    &__retry {
      background: transparent;
      border: 1px solid currentColor;
      color: inherit;
      padding: $dims-field-error-margin $dims-spacing-small;
      cursor: pointer;
      font: inherit;

      &:focus {
        outline: none;
      }
    }
  }
</style>
